import axios from "axios";
import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  ButtonGroup,
  Heading,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableDataCell,
} from "blueprints2";

import { CredentialContext } from "../context/CredentialContext";
import CredentialTableEmpty from "./CredentialTableEmpty";
import DeleteCredential from "./forms/DeleteCredential";
import EditCredential from "./forms/EditCredential";
import GenerateToken from "./forms/GenerateToken";
import { isUnique, isLengthValid, isScopeValid } from "../helpers";
import { FormattedMessage, injectIntl } from "react-intl";
import CredentialAlert, { AlertTypes } from "./CredentialAlert";

function CredentialTable({ intl, credentialData, workspace }) {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [tokenModalVisible, setTokenModalVisible] = useState(false);
  const [formValues, setFormValues] = useState({
    name: "",
    clientId: "",
    clientSecret: "",
    consumer: {},
    createdAt: null,
    id: null,
    bearer: "",
  });
  const [credentials, setCredentials] = useState(credentialData);
  const [modalError, setModalError] = useState("");
  const [alert, setAlert] = useState(null);
  const { credentialState, updateCredentials } = useContext(CredentialContext);

  const credentialsUrl = "/" + workspace + "/credentials/oauth2";
  const tokenUrl = process.env.REACT_APP_GATEWAY_URL + "/token";

  const editCred = (e) => {
    // prevent submit & validate input
    e.preventDefault();

    if (!isLengthValid(formValues.name, credentialState)) {
      setModalError(intl.formatMessage({ id: "applications.form.error.size" }));
      return;
    }

    if (!isUnique(formValues.name, credentialState)) {
      setModalError(intl.formatMessage({ id: "applications.form.error.unique" }));
      return;
    }

    // build & make request
    axios({
      method: "patch",
      url: credentialsUrl + "/" + formValues.id,
      withCredentials: true,
      data: {
        name: formValues.name,
        client_id: formValues.clientId,
        client_secret: formValues.clientSecret,
        consumer: formValues.consumer,
        id: formValues.id,
        created_at: formValues.createdAt,
      },
    })
      // hide modal & get new data
      .then(() => {
        setEditModalVisible(false);
        return axios.get(credentialsUrl, { withCredentials: true });
      })
      // update credentialData context & alert
      .then((res) => {
        const credentialData = res.data.data;
        updateCredentials(credentialData);
        const extraData = {
          appName: formValues.name,
        };
        setAlert({ type: AlertTypes.EDIT, extraData });
      })

      // catch errs from patch or get
      .catch((err) => {
        setEditModalVisible(false);
        setAlert({ type: AlertTypes.ERROR });
      });
  };

  const deleteCred = (e) => {
    // prevent submit & make request
    e.preventDefault();
    axios({
      method: "delete",
      url: credentialsUrl + "/" + formValues.id,
      withCredentials: true,
    })
      // hide modal & get new data
      .then(() => {
        setDeleteModalVisible(false);
        return axios.get(credentialsUrl, { withCredentials: true });
      })
      // update credentialData context & alert
      .then((res) => {
        const credentialData = res.data.data;
        updateCredentials(credentialData);
        const extraData = {
          appName: formValues.name,
        };
        setAlert({ type: AlertTypes.DELETE, extraData });
      })

      // catch errs from delete or get
      .catch((err) => {
        setDeleteModalVisible(false);
        setAlert({ type: AlertTypes.ERROR });
      });
  };

  const generateToken = (e) => {
    // prevent submit & store values
    e.preventDefault();
    const sid = document.getElementById("scope-sid").value;
    const bearerAuth = `Bearer ${formValues.bearer}`;

    // validate scope
    if (!isScopeValid(sid)) {
      setModalError(
        intl.formatMessage({ id: "applications.form.error.scope" })
      );
      return;
    }

    // set data & headers
    const data = `scope=${sid}`;
    const headers = {
      Authorization: bearerAuth,
      "Content-Type": "application/x-www-form-urlencoded",
    };

    // build & make request
    axios({
      method: "post",
      url: tokenUrl,
      headers: headers,
      data: data,
    })
      // store token response & alert
      .then((res) => {
        const token = res.data.access_token;
        const extraData = {
          app: formValues.name,
          token: token,
        };
        setTokenModalVisible(false);
        setAlert({ type: AlertTypes.TOKEN, extraData });
      })
      // catch token request err
      .catch((err) => {
        if (err?.response?.status === 401) {
          setModalError(
            intl.formatMessage({
              id: "applications.form.error.sid",
              defaultMessage: "Invalid SID",
            })
          );
          return;
        }
        if (err?.response?.status === 429) {
          setModalError(
            intl.formatMessage({
              id: "applications.form.error.rate",
              defaultMessage: "Rate limit exceeded",
            })
          );
          return;
        }
        setTokenModalVisible(false);
        setAlert({ type: AlertTypes.ERROR });
      });
  };

  const prepModalValues = (cred, modal) => {
    let bearer = Buffer.from(
      `${cred.client_id}:${cred.client_secret}`
    ).toString("base64");
    setFormValues({
      name: cred.name,
      clientId: cred.client_id,
      clientSecret: cred.client_secret,
      consumer: cred.consumer,
      createdAt: cred.created_at,
      id: cred.id,
      bearer: bearer,
    });
    setModalError("");
    switch (modal) {
      case "delete":
        setDeleteModalVisible(true);
        break;
      case "token":
        setTokenModalVisible(true);
        break;
      default:
        setEditModalVisible(true);
        break;
    }
  };

  const formChange = (e) => {
    const fieldValue = e.value;
    setFormValues({ ...formValues, name: fieldValue });
  };

  const resetValues = () => {
    setFormValues({
      name: "",
      clientId: "",
      clientSecret: "",
      consumer: {},
      createdAt: null,
      id: null,
      bearer: "",
    });
  };

  useEffect(() => {
    // set table credentials from context
    setCredentials(credentialState);
    resetValues();
  }, [credentialState]);
  return credentials?.length < 1 ? (
    <CredentialTableEmpty />
  ) : (
    <div className="react_page__component">
      <CredentialAlert className="other_alert" {...alert} />

      <Heading level={3} className="pt-4">
        OAuth 2.0
      </Heading>
      <Table hasHover>
        <TableHeader>
          <TableHeaderCell className="font-semibold text-base">
            <FormattedMessage id="applications.table.header.name" />
          </TableHeaderCell>
          <TableHeaderCell className="font-semibold text-base">
            <FormattedMessage id="applications.table.header.client_id" />
          </TableHeaderCell>
          <TableHeaderCell className="font-semibold text-base">
            <FormattedMessage id="applications.table.header.client_secret" />
          </TableHeaderCell>
          <TableHeaderCell className="font-semibold text-base">
            <FormattedMessage id="applications.table.header.actions" />
          </TableHeaderCell>
        </TableHeader>
        {credentials.map((credential, index) => {
          return (
            <TableRow key={index}>
              <TableHeaderCell
                className="normalize-text normal-case"
                scope="row"
              >
                {credential.name}
              </TableHeaderCell>
              <TableDataCell>{credential.client_id}</TableDataCell>
              <TableDataCell>{credential.client_secret}</TableDataCell>
              <TableDataCell className="Table__TableData--center">
                <Button
                  isSmall
                  icon="key"
                  onClick={() => prepModalValues(credential, "token")}
                >
                  <FormattedMessage id="applications.table.button.token" />
                </Button>
                <ButtonGroup isSecondary isSmall>
                  <Button
                    icon="edit"
                    onClick={() => prepModalValues(credential, "edit")}
                  >
                    <FormattedMessage id="applications.table.button.edit" />
                  </Button>
                  <Button
                    icon="trash"
                    onClick={() => prepModalValues(credential, "delete")}
                  >
                    <FormattedMessage id="applications.table.button.delete" />
                  </Button>
                </ButtonGroup>
              </TableDataCell>
            </TableRow>
          );
        })}
      </Table>

      {/* Modals */}
      <EditCredential
        visible={editModalVisible}
        setVisible={setEditModalVisible}
        values={formValues}
        change={formChange}
        editCred={editCred}
        error={modalError}
      />

      <DeleteCredential
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
        values={formValues}
        deleteCred={deleteCred}
      />

      <GenerateToken
        visible={tokenModalVisible}
        setVisible={setTokenModalVisible}
        values={formValues}
        generateToken={generateToken}
        modalError={modalError}
      />
    </div>
  );
}

export default injectIntl(CredentialTable);
